/**
 * Tourelle de forteresse (phase D) : fixe, pivote vers la position anticipée du joueur et tire des rafales
 * courtes, canons alternés. Ne tire que si le joueur vole, est à portée, en face et en vue (la forteresse masque).
 */
import type { EnemiesData } from '../../../data';
import { Quat, Vec3, UP } from '../../core/math';
import { emptyCommand, Ship, type ShipCommand } from '../../entities';
import { aiMotorProfile, turnRateToward } from '../../flight/motor';
import type { SimState } from '../../state';
import { spawnEnemyBolt } from '../../weapons/projectiles';
import { EnemyBrain } from './enemy';

type TurretProfile = EnemiesData['roles']['turret'];

export class TurretBrain extends EnemyBrain {
  private burstLeft = 0;
  private barrel = 1;

  private get turret(): TurretProfile {
    return this.profile as TurretProfile;
  }

  override update(dt: number): ShipCommand {
    const cmd = emptyCommand();
    cmd.brake = true;
    this.age += dt;
    const state = this.state;
    const player = state.player;
    if (!player.alive || state.control.mode !== 'flight') {
      this.burstLeft = 0;
      this.intent = 'veille';
      return cmd;
    }
    const p = this.turret;
    const common = state.data.enemies.common;
    const ship = this.ship;
    const toPlayer = player.position.sub(ship.position);
    const flightTime = toPlayer.length() / Math.max(p.projectileSpeed, 1);
    const aim = player.position.add(player.velocity.scale(Math.min(common.maxLeadTime, flightTime * common.leadFactor)));
    const aimDir = aim.sub(ship.position).normalized(ship.forward());
    cmd.turnRate = turnRateToward(ship.rotation, Quat.lookRotation(aimDir, UP()), ship.motor.maxTotalRate, dt);
    this.intent = toPlayer.lengthSq() <= p.fireRange ** 2 ? 'visée' : 'veille';

    this.timeUntilNextShot -= dt;
    if (this.timeUntilNextShot > 0) return cmd;
    if (this.burstLeft === 0) {
      // Début de rafale : portée, alignement des canons, ligne de vue.
      const origin = this.weaponOrigin();
      const blocked = toPlayer.lengthSq() > p.fireRange ** 2
        || ship.forward().dot(aimDir) < p.requiredFacingDot
        || !state.collision.lineOfSight(origin, player.position);
      if (blocked) {
        this.timeUntilNextShot = Math.min(p.fireInterval, common.blockedRetryDelay);
        return cmd;
      }
      this.burstLeft = p.burst;
    }
    this.barrel = -this.barrel;
    spawnEnemyBolt(state, {
      kind: 'bolt',
      owner: ship,
      source: 'turret',
      origin: this.weaponOrigin().add(ship.right().scale(this.barrel * 1.6)),
      direction: ship.forward(),
      speed: p.projectileSpeed,
      range: p.fireRange,
      damage: p.projectileDamage,
      color: p.boltColor,
    });
    this.burstLeft--;
    this.timeUntilNextShot = this.burstLeft > 0 ? p.burstInterval : p.fireInterval;
    return cmd;
  }

  override onBlocked(_normal: Vec3): void {}
}

/** Crée une tourelle fixe et son IA. */
export function createTurret(state: SimState, position: Vec3, rotation: Quat): { ship: Ship; brain: TurretBrain } {
  const data = state.data.enemies;
  const profile = data.roles.turret;
  const motor = aiMotorProfile({
    maxSpeed: profile.maxSpeed,
    acceleration: profile.acceleration,
    turnRateDeg: profile.turnRateDeg,
    releaseDeceleration: data.common.releaseDeceleration,
    brakeDeceleration: data.common.brakeDeceleration,
    turningBoost: 0,
  });
  const ship = new Ship(state.newId(), 'turret', 'enemy', position, rotation, profile.health, profile.collisionRadius, motor);
  return { ship, brain: new TurretBrain(state, ship, 'turret', profile) };
}
